import { DELAY_TIME_3000, DELAY_TIME_5000 } from "@configs/constants";
import { ENV_CONFIG } from "@configs/env";

const ANIMATION_CANVAS_ID = "gimmick";
const PREFERRED_ANIMATION_CONTAINER_ID = "service_block";
const COIN_IMAGE_SRC = `${ENV_CONFIG.BASE_BUCKET_URL}/coin.png`;

const COIN_SPAWN_PROBABILITY = 0.3;
const COIN_START_Y = -50;
const COIN_INITIAL_VELOCITY_Y = 1;
const COIN_GRAVITY = 0.3;
const COIN_MIN_SCALE = 0.1;

const SPRITE_FRAME_WIDTH = 44;
const SPRITE_FRAME_HEIGHT = 40;
const SPRITE_FRAME_COUNT = 10;
const SPRITE_FRAME_STEP = 0.1;

const COIN_RENDER_WIDTH = 104;

type TCoinParticle = {
	x: number;
	y: number;
	velocityY: number;
	scale: number;
	spriteFrame: number;
};

type TMoneyAnimationSession = {
	canvas: HTMLCanvasElement;
	context: CanvasRenderingContext2D;
	coinImage: HTMLImageElement;
	coins: TCoinParticle[];
	frameId: number | null;
	timeoutIds: number[];
	isRunning: boolean;
	isSpawning: boolean;
};

type TCanvasContext = {
	canvas: HTMLCanvasElement;
	context: CanvasRenderingContext2D;
};

let activeSession: TMoneyAnimationSession | null = null;

const createCanvasContext = (): TCanvasContext | null => {
	const canvas = document.createElement("canvas");
	const context = canvas.getContext("2d");

	if (!context) return null;

	canvas.id = ANIMATION_CANVAS_ID;
	canvas.width = window.innerWidth;
	canvas.height = window.innerHeight;

	return { canvas, context };
};

const getAnimationContainer = (): HTMLElement => {
	const preferredContainer = document.getElementById(PREFERRED_ANIMATION_CONTAINER_ID);

	return preferredContainer ?? document.body;
};

const createCoinParticle = (canvasWidth: number): TCoinParticle => ({
	x: Math.floor(Math.random() * canvasWidth),
	y: COIN_START_Y,
	velocityY: COIN_INITIAL_VELOCITY_Y,
	scale: COIN_MIN_SCALE + Math.random(),
	spriteFrame: Math.floor(Math.random() * SPRITE_FRAME_COUNT),
});

const drawCoin = (context: CanvasRenderingContext2D, coinImage: HTMLImageElement, coin: TCoinParticle): void => {
	context.drawImage(
		coinImage,
		SPRITE_FRAME_WIDTH * Math.floor(coin.spriteFrame),
		0,
		SPRITE_FRAME_WIDTH,
		SPRITE_FRAME_HEIGHT,
		coin.x,
		coin.y,
		COIN_RENDER_WIDTH * coin.scale,
		SPRITE_FRAME_HEIGHT * coin.scale,
	);
};

const updateCoin = (coin: TCoinParticle): void => {
	coin.spriteFrame = coin.spriteFrame > SPRITE_FRAME_COUNT - 1 ? 0 : coin.spriteFrame + SPRITE_FRAME_STEP;
	coin.velocityY += COIN_GRAVITY;
	coin.y += coin.velocityY;
};

const removeCoinIfOutsideCanvas = (coins: TCoinParticle[], index: number, canvasHeight: number): void => {
	if (coins[index].y > canvasHeight) {
		coins.splice(index, 1);
	}
};

const stopAnimationSession = (session: TMoneyAnimationSession): void => {
	session.isRunning = false;
	session.isSpawning = false;

	if (session.frameId !== null) {
		cancelAnimationFrame(session.frameId);
	}

	session.timeoutIds.forEach((timeoutId) => {
		window.clearTimeout(timeoutId);
	});
	session.canvas.remove();

	if (activeSession === session) {
		activeSession = null;
	}
};

const drawAnimationFrame = (session: TMoneyAnimationSession): void => {
	if (!session.isRunning) return;

	const { canvas, context, coinImage, coins } = session;

	context.clearRect(0, 0, canvas.width, canvas.height);

	if (session.isSpawning && Math.random() < COIN_SPAWN_PROBABILITY) {
		coins.push(createCoinParticle(canvas.width));
	}

	for (let index = coins.length - 1; index >= 0; index -= 1) {
		const coin = coins[index];

		drawCoin(context, coinImage, coin);
		updateCoin(coin);
		removeCoinIfOutsideCanvas(coins, index, canvas.height);
	}

	session.frameId = requestAnimationFrame(() => drawAnimationFrame(session));
};

const startAnimationSession = (session: TMoneyAnimationSession): void => {
	getAnimationContainer().appendChild(session.canvas);

	session.isRunning = true;
	session.isSpawning = true;
	session.timeoutIds = [
		window.setTimeout(() => {
			session.isSpawning = false;
		}, DELAY_TIME_3000),
		window.setTimeout(() => {
			stopAnimationSession(session);
		}, DELAY_TIME_5000),
	];

	drawAnimationFrame(session);
};

export const moneyAnimation = (): void => {
	if (typeof document === "undefined" || typeof window === "undefined") return;

	if (activeSession) {
		stopAnimationSession(activeSession);
		return;
	}

	const existingCanvas = document.getElementById(ANIMATION_CANVAS_ID);

	if (existingCanvas) {
		existingCanvas.remove();
		return;
	}

	const canvasContext = createCanvasContext();

	if (!canvasContext) return;

	const session: TMoneyAnimationSession = {
		...canvasContext,
		coinImage: new Image(),
		coins: [],
		frameId: null,
		timeoutIds: [],
		isRunning: false,
		isSpawning: false,
	};

	activeSession = session;

	session.coinImage.onload = () => {
		if (activeSession !== session) return;

		startAnimationSession(session);
	};

	session.coinImage.onerror = () => {
		if (activeSession === session) {
			activeSession = null;
		}
	};

	session.coinImage.src = COIN_IMAGE_SRC;
};
